import React, { useState } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import SectionHeading from '../components/ui/SectionHeading';
import Button from '../components/ui/Button';
import { PhoneCall, Mail, Clock, Calendar, ExternalLink } from 'lucide-react';
import ConsultationForm from '../components/ui/ConsultationForm';

const Contact: React.FC = () => {
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append('name', formData.name);
      formDataObj.append('email', formData.email);
      formDataObj.append('phone', formData.phone);
      formDataObj.append('subject', formData.subject);
      formDataObj.append('message', formData.message);
      formDataObj.append('form-name', 'contact');

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as any).toString(),
      });

      if (response.ok) {
        toast.success('Message sent successfully!');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-[#FFF8ED] relative">
      <Toaster position="top-center" />
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#FAFAF5] to-transparent"></div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <SectionHeading 
          title="Get in Touch" 
          subtitle="Have questions or concerns? We're here to help and guide you."
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-light rounded-lg shadow-custom p-6 md:p-8 border-2 border-[#3087cf]">
            <h3 className="font-abadi text-xl font-medium mb-6">Message Us</h3>
            <form
              className="space-y-4"
              onSubmit={handleSubmit}
              name="contact"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden">
                <label>Don't fill this out: <input name="bot-field" /></label>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-dark-gray mb-1">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-dark-gray mb-1">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
                    placeholder="john@example.com"
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="phone" className="block text-dark-gray mb-1">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
                  placeholder="(424) 277-7216"
                />
              </div>
              <div>
                <label htmlFor="subject" className="block text-dark-gray mb-1">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
                  placeholder="Property Management Inquiry"
                  required
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-dark-gray mb-1">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
                  placeholder="Tell us about your property and management needs..."
                  required
                ></textarea>
              </div>
              <Button variant="primary" type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>
          
          {/* Contact Info & Booking */}
          <div className="flex flex-col">
            {/* Contact */}
            <div className="bg-light rounded-lg shadow-custom p-6 md:p-8 mb-8 border-2 border-[#3087cf]">
              <h3 className="font-abadi text-xl font-medium mb-6">Contact</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-primary/20 p-3 rounded-full mr-4">
                    <PhoneCall className="h-6 w-6 text-secondary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Call Us</h4>
                    <a 
                      href="tel:+14242777216"
                      className="text-dark-gray hover:text-secondary transition-colors duration-200"
                    >
                      (424) 277-7216
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-primary/20 p-3 rounded-full mr-4">
                    <Mail className="h-6 w-6 text-secondary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Email Us</h4>
                    <a 
                      href="mailto:info@rentsparks.com"
                      className="text-dark-gray hover:text-secondary transition-colors duration-200"
                    >
                      info@rentsparks.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-primary/20 p-3 rounded-full mr-4">
                    <Calendar className="h-6 w-6 text-secondary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Schedule Online</h4>
                    <a 
                      href="https://calendly.com/rentsparks"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dark-gray hover:text-secondary transition-colors duration-200 inline-flex items-center gap-1"
                    >
                      Book via Calendly
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-primary/20 p-3 rounded-full mr-4">
                    <Clock className="h-6 w-6 text-secondary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Business Hours</h4>
                    <p className="text-dark-gray">Mon - Fri: 9:00 AM - 5:00 PM</p>
                    <p className="text-dark-gray">Sat - Sun: 10:00 AM - 2:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Schedule a Call */}
            <div className="bg-gradient-to-br from-[#57a0e0] to-[#2c6fb0] text-white rounded-lg shadow-[0_20px_45px_-12px_rgba(27,79,114,0.5)] p-6 md:p-8 flex-grow">
              <div className="flex items-center mb-6">
                <Calendar className="h-7 w-7 mr-3" strokeWidth={1.5} />
                <h3 className="font-abadi text-xl font-medium">Schedule a Call</h3>
              </div>
              <p className="mb-6">
                Book a 15-minute consultation with one of our property management experts to discuss your specific needs and how we can help.
              </p>
              <Button
                variant="primary"
                className="w-full"
                onClick={() => setShowConsultationForm(true)}
              >
                Schedule a Call
              </Button>
            </div>
          </div>
        </div>
      </div>
      {showConsultationForm && (
        <ConsultationForm onClose={() => setShowConsultationForm(false)} />
      )}
    </section>
  );
};

export default Contact;